import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Typography,
    CircularProgress,
    Box,
    Alert,
    FormControl,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { apiHandler } from '../../../../utils/api/responseHandler';
import { gradeOptionsValidator } from '../../../../utils/api/validation';
import type { GenericOption, BattenOptionNew, GradeOption, QuoteData } from '../../../../types/quoter/types';
import config from '../../../../config';
import logger from '../../../../utils/logging/logger';
import { useComponentVersion } from '../../../../hooks/quoter/useComponentVersion';
import SegmentedControl from './SegmentedControl';
import CollapsibleOptionRow from './CollapsibleOptionRow';

const componentName = 'OptionsDropdowns';

interface OptionsDropdownsProps {
    quoteId: number;
    boatId: number;
    sailId: number;
    sailArea: number;
    isFurling: number;
}

interface OptionsState {
    genericOptions: GenericOption[];
    battenOptions: BattenOptionNew[];
    gradeOptions: GradeOption[];
    selectedGradeId: string;
    fullBattens: number;
    halfBattens: number;
    percentLP: number;
    defaultLP: number;
    isLoaded: boolean;
    gradeLoaded: boolean;
    lpLoaded: boolean;
    error: string | null;
}

// Helper to format price delta
const formatPriceDelta = (priceDelta: number): string => {
    if (Math.abs(priceDelta) < 1) return '';
    const absValue = Math.abs(Math.round(priceDelta));
    return priceDelta > 0 ? ' (+$' + absValue + ')' : ' (-$' + absValue + ')';
};

const OptionsDropdowns: React.FC<OptionsDropdownsProps> = ({
    quoteId,
    boatId,
    sailId,
    sailArea,
    isFurling
}) => {
    logger.log('LogComponentUpdates', componentName, 'Component rendering with props:', { quoteId, boatId, sailId, sailArea });

    const updatingRef = useRef<{ [key: string]: boolean; battens_full: boolean; battens_half: boolean; cloth: boolean; lp: boolean }>({
    battens_full: false,
    battens_half: false,
    cloth: false,
        lp: false
    });

    // Track which option row is expanded (only one at a time)
    const [expandedOption, setExpandedOption] = useState<string | null>(null);

    const [state, setState] = useState<OptionsState>({
        genericOptions: [],
        battenOptions: [],
        gradeOptions: [],
        selectedGradeId: '',
        fullBattens: 0,
        halfBattens: 0,
        percentLP: 100,
        defaultLP: 100,
        isLoaded: false,
        gradeLoaded: false,
        lpLoaded: false,
        error: null
    });

    const fetchOptions = useCallback(async () => {
        if (!quoteId) return;

        const controller = new AbortController();
        const signal = controller.signal;

        try {
            logger.log('LogComponentUpdates', componentName, 'fetchOptions: initialized');

            setState(prev => ({
                ...prev,
                isLoaded: false,
                error: null
            }));

            const response = await apiHandler.fetch<GenericOption[]>(
                config.API_URL + '/api/quotes/' + quoteId + '/options',
                { signal: signal }
            );

            if (response.error) throw response.error;

            const options = response.data || [];
            let battens: BattenOptionNew[] = [];

            // Only fetch battens for mainsails
            if ([1, 2].includes(sailId)) {
                const battenResponse = await apiHandler.fetch<BattenOptionNew[]>(
                    config.API_URL + '/api/quotes/' + quoteId + '/battens',
                    { signal: controller.signal }
                );

                if (battenResponse.error) throw battenResponse.error;
                battens = Array.isArray(battenResponse.data) ? battenResponse.data : [];
            }

            setState(prev => ({
                ...prev,
                genericOptions: options,
                battenOptions: battens,
                fullBattens: battens.length > 0 ? (battens.find(b => b.IsSelected === 1)?.FullBattens || 0) : 0,
                halfBattens: battens.length > 0 ? (battens.find(b => b.IsSelected === 1)?.HalfBattens || 0) : 0,
                isLoaded: true,
                error: null
            }));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sail options';
            console.error('OptionsDropdowns: Error fetching data:', errorMessage);

            setState(prev => ({
                ...prev,
                error: errorMessage,
                isLoaded: true
            }));
        }
    }, [quoteId, sailId]);

    const fetchClothOptions = useCallback(async () => {
        if (quoteId <= 0) return;

        try {
            const gradeResponse = await apiHandler.fetch<GradeOption[]>(
                `${config.API_URL}/api/quotes/${quoteId}/cloth-grade-override?_t=${Date.now()}`,
                { validateFn: (data) => gradeOptionsValidator.validate(data) }
            );

            if (gradeResponse.error) throw gradeResponse.error;

            const gradeData = gradeResponse.data || [];
            const selectedGrade = gradeData.find(opt => opt.IsSelected);

            setState(prev => ({
                ...prev,
                gradeOptions: gradeData,
                selectedGradeId: selectedGrade?.ClothOverrideGradeId.toString() || '',
                gradeLoaded: true
            }));
        } catch (err) {
            console.error('OptionsDropdowns: Error fetching cloth options:', err);
        }
    }, [quoteId]);

    // Fetch LP data for headsails
    const fetchLPData = useCallback(async () => {
        // Only fetch LP for headsails (not mainsails which are sailId 1, 2)
        if (quoteId <= 0 || [1, 2].includes(sailId)) return;

        try {
            const response = await apiHandler.fetch<QuoteData>(
                `${config.API_URL}/api/quotes/${quoteId}`
            );

            if (response.error) throw response.error;

            if (response.data) {
                setState(prev => ({
                    ...prev,
                    percentLP: response.data?.PercentLP || 100,
                    defaultLP: 100, // Default LP is typically 100%
                    lpLoaded: true
                }));
            }
        } catch (err) {
            console.error('OptionsDropdowns: Error fetching LP data:', err);
        }
    }, [quoteId, sailId]);

    const { requestVersionUpdate } = useComponentVersion('sailOptions', {
        onVersionChange: () => {
            fetchOptions();
            fetchClothOptions();
            fetchLPData();
        }
    });

    useComponentVersion('clothOverrides', {
        onVersionChange: fetchClothOptions
    });

    useComponentVersion('lpAdjuster', {
        onVersionChange: fetchLPData
    });

    useEffect(() => {
        logger.log('LogComponentUpdates', componentName, 'Initial fetch effect running');
        fetchOptions();
        fetchClothOptions();
        fetchLPData();
    }, [fetchOptions, fetchClothOptions, fetchLPData]);

    const handleGenericOptionUpdate = useCallback(async (optionId: number, value: number) => {
        const optionKey = 'option_' + optionId;
        if (updatingRef.current[optionKey]) return;
        updatingRef.current[optionKey] = true;

        try {
            setState(prev => ({ ...prev, error: null }));

            const response = await apiHandler.fetch(
                config.API_URL + '/api/quotes/option/' + optionId,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ valueSelected: value })
                }
            );

            if (response.error) throw response.error;

            setState(prev => ({
                ...prev,
                genericOptions: prev.genericOptions.map(opt =>
                    opt.QuoteOptionId === optionId
                        ? { ...opt, ValueSelected: value }
                        : opt
                )
            }));

            requestVersionUpdate();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update option';
            setState(prev => ({ ...prev, error: errorMessage }));
        } finally {
            updatingRef.current[optionKey] = false;
        }
    }, [requestVersionUpdate]);

    const handleBattenUpdate = useCallback(async (value: number) => {
        const updateKey = 'battens_full';
        if (updatingRef.current[updateKey]) return;
        updatingRef.current[updateKey] = true;

        try {
            setState(prev => ({ ...prev, error: null }));

            const newCount = isFurling === 1
                ? { fullBattens: 0, halfBattens: value }
                : { fullBattens: value, halfBattens: 0 };

            const response = await apiHandler.fetch(
                config.API_URL + '/api/quotes/' + quoteId + '/update-battens',
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newCount)
                }
            );

            if (response.error) throw response.error;

            setState(prev => ({
                ...prev,
                fullBattens: isFurling === 1 ? prev.fullBattens : value,
                halfBattens: isFurling === 1 ? value : prev.halfBattens
            }));

            requestVersionUpdate();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update battens';
            setState(prev => ({ ...prev, error: errorMessage }));
        } finally {
            updatingRef.current[updateKey] = false;
        }
    }, [quoteId, isFurling, requestVersionUpdate]);

    const handleGradeUpdate = useCallback(async (event: SelectChangeEvent<string>) => {
        const gradeId = event.target.value;
        if (updatingRef.current.cloth) return;
        updatingRef.current.cloth = true;

        try {
            const response = await apiHandler.fetch(
                `${config.API_URL}/api/quotes/${quoteId}/cloth-grade`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ clothId_GradeSelection: parseInt(gradeId) })
                }
            );

            if (response.error) throw response.error;

            setState(prev => ({ ...prev, selectedGradeId: gradeId }));
            requestVersionUpdate();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update cloth';
            setState(prev => ({ ...prev, error: errorMessage }));
        } finally {
            updatingRef.current.cloth = false;
        }
    }, [quoteId, requestVersionUpdate]);

    const handleLPUpdate = useCallback(async (event: SelectChangeEvent<string>) => {
        const newValue = parseInt(event.target.value);
        if (isNaN(newValue) || updatingRef.current.lp) return;
        updatingRef.current.lp = true;

        try {
            const response = await apiHandler.fetch(
                `${config.API_URL}/api/quotes/${quoteId}/lp`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ percentLP: newValue })
                }
            );

            if (response.error) throw response.error;

            setState(prev => ({ ...prev, percentLP: newValue }));
            requestVersionUpdate();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update LP';
            setState(prev => ({ ...prev, error: errorMessage }));
        } finally {
            updatingRef.current.lp = false;
        }
    }, [quoteId, requestVersionUpdate]);

    // Determine if option should use segmented control
    const shouldUseSegmented = (option: GenericOption): boolean => {
        const isYesNo = option.IsRangeYesNo === 1 ||
            (option.SelectRangeMin === 0 && option.SelectRangeMax === 1);
        if (isYesNo) return true;

        const hasValidRange = option.SelectRangeMin > -1 && option.SelectRangeMax > -1;
        if (hasValidRange) {
            const rangeSize = option.SelectRangeMax - option.SelectRangeMin;
            return rangeSize <= 5;
        }
        return false;
    };

    // Generate options for segmented control
    const getSegmentedOptions = (option: GenericOption) => {
        const isYesNo = option.IsRangeYesNo === 1 ||
            (option.SelectRangeMin === 0 && option.SelectRangeMax === 1);

        if (isYesNo) {
            const noPrice = option.OptionQuotedPrice * (0 - option.DefaultValue);
            const yesPrice = option.OptionQuotedPrice * (1 - option.DefaultValue);
            return [
                { 
                    value: 0, 
                    label: option.DefaultValue === 0 ? 'No ✓' : 'No' + formatPriceDelta(noPrice), 
                    isDefault: option.DefaultValue === 0 
                },
                { 
                    value: 1, 
                    label: option.DefaultValue === 1 ? 'Yes ✓' : 'Yes' + formatPriceDelta(yesPrice), 
                    isDefault: option.DefaultValue === 1 
                }
            ];
        }

        const options = [];
        for (let i = option.SelectRangeMin; i <= option.SelectRangeMax; i++) {
            const priceDelta = option.OptionQuotedPrice * (i - option.DefaultValue);
            let label = i.toString();
            if (i === option.DefaultValue) {
                label += ' ✓';
            } else if (priceDelta !== 0) {
                label += formatPriceDelta(priceDelta);
            }
            options.push({ value: i, label, isDefault: i === option.DefaultValue });
        }
        return options;
    };

    // Generate dropdown options
    const getDropdownOptions = (option: GenericOption) => {
        const options = [];
        for (let i = option.SelectRangeMin; i <= option.SelectRangeMax; i++) {
            const priceDelta = option.OptionQuotedPrice * (i - option.DefaultValue);
            let label = i.toString();
            if (i === option.DefaultValue) {
                label += ' (recommended)';
            } else if (priceDelta !== 0) {
                label += formatPriceDelta(priceDelta);
            }
            options.push({ value: i, label });
        }
        return options;
    };

    // Get current value display text for an option
    const getOptionDisplayValue = (option: GenericOption): { text: string; isDefault: boolean } => {
        const isYesNo = option.IsRangeYesNo === 1 ||
            (option.SelectRangeMin === 0 && option.SelectRangeMax === 1);
        
        const isDefault = option.ValueSelected === option.DefaultValue;
        
        if (isYesNo) {
            return { 
                text: option.ValueSelected === 1 ? 'Included' : 'Not included', 
                isDefault 
            };
        }
        return { text: option.ValueSelected.toString(), isDefault };
    };

    // Build a unified render list preserving exact database order
    // Grouped options are rendered at the position of their first item
    const buildRenderList = () => {
        const renderList: Array<{ type: 'option'; option: GenericOption } | { type: 'group'; groupCode: string; group: { name: string; items: GenericOption[] } }> = [];
        const groupedOptions: Record<string, { name: string; items: GenericOption[] }> = {};
        const processedGroups = new Set<string>();

        state.genericOptions.forEach(opt => {
            if (opt.OptionGroupCode) {
                // Collect grouped option items
                if (!groupedOptions[opt.OptionGroupCode]) {
                    groupedOptions[opt.OptionGroupCode] = {
                        name: opt.OptionGroupName,
                        items: []
                    };
                }
                groupedOptions[opt.OptionGroupCode].items.push(opt);

                // Add group to render list at position of first item
                if (!processedGroups.has(opt.OptionGroupCode)) {
                    processedGroups.add(opt.OptionGroupCode);
                    renderList.push({ type: 'group', groupCode: opt.OptionGroupCode, group: groupedOptions[opt.OptionGroupCode] });
                }
            } else {
                // Regular option - add directly
                renderList.push({ type: 'option', option: opt });
            }
        });

        return { renderList };
    };

    if (!state.isLoaded) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading options...</Typography>
            </Box>
        );
    }

    if (state.error) {
        return (
            <Alert severity="error" sx={{ my: 1 }}>
                {state.error}
            </Alert>
        );
    }

    const { renderList } = buildRenderList();
    const allOptionKeys: string[] = [];

    // Build list of all option keys for divider logic (preserving render order)
    const enabledGradeOptions = state.gradeOptions.filter(opt => opt.IsEnabled);
    const showClothOption = state.gradeLoaded && enabledGradeOptions.length > 1;

    if (showClothOption) {
        allOptionKeys.push('cloth');
    }

    if (state.battenOptions.length > 0) {
        allOptionKeys.push('battens');
    }

    renderList.forEach(item => {
        if (item.type === 'option') {
            allOptionKeys.push('option_' + item.option.QuoteOptionId);
        } else {
            allOptionKeys.push('group_' + item.groupCode);
        }
    });

    // Render cloth option row
    const renderClothRow = () => {
        if (!showClothOption) return null;

        const selectedGrade = state.gradeOptions.find(opt => opt.ClothOverrideGradeId.toString() === state.selectedGradeId);
        const isDefault = selectedGrade?.IsSelected === 1;
        const currentIndex = allOptionKeys.indexOf('cloth');
        const isLast = currentIndex === allOptionKeys.length - 1;

        return (
            <CollapsibleOptionRow
                key="cloth"
                optionKey="cloth"
                label="Sailcloth"
                currentValue={selectedGrade?.ClothOverrideDescription || ''}
                isDefault={isDefault}
                isExpanded={expandedOption === 'cloth'}
                onToggle={setExpandedOption}
                showDivider={!isLast}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="body1" fontWeight={500}>
                            Sailcloth
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            Premium cloth affects durability and performance
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
                        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 280 }, width: { xs: '100%', sm: 280 } }}>
                            <Select
                                value={state.selectedGradeId}
                                onChange={handleGradeUpdate}
                                disabled={updatingRef.current.cloth}
                                sx={{
                                    height: 'auto',
                                    minHeight: 40,
                                    '& .MuiSelect-select.MuiSelect-outlined': {
                                        whiteSpace: 'normal !important',
                                        overflow: 'visible !important',
                                        textOverflow: 'clip !important',
                                        minHeight: 'auto',
                                        py: 1,
                                    },
                                }}
                            >
                                {enabledGradeOptions.map((option) => {
                                    const priceDelta = option.PriceDelta;
                                    let modifier = '';
                                    if (priceDelta === 0) {
                                        modifier = ' (recommended)';
                                    }
                                    // TODO: Re-enable price delta display when pricing is finalized
                                    // else if (priceDelta > 0) {
                                    //     modifier = ` (+$${Math.round(priceDelta)})`;
                                    // } else if (priceDelta < 0) {
                                    //     modifier = ` (-$${Math.abs(Math.round(priceDelta))})`;
                                    // }
                                    return (
                                        <MenuItem
                                            key={option.ClothOverrideGradeId}
                                            value={option.ClothOverrideGradeId.toString()}
                                            sx={{ whiteSpace: 'normal' }}
                                        >
                                            {option.ClothOverrideDescription}{modifier}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        {updatingRef.current.cloth && <CircularProgress size={20} />}
                    </Box>
                </Box>
            </CollapsibleOptionRow>
        );
    };

    // Render a single option row
    const renderOptionRow = (option: GenericOption) => {
        const optionKey = 'option_' + option.QuoteOptionId;
        const useSegmented = shouldUseSegmented(option);
        const { text: currentValue, isDefault } = getOptionDisplayValue(option);
        const currentIndex = allOptionKeys.indexOf(optionKey);
        const isLast = currentIndex === allOptionKeys.length - 1;

        return (
            <CollapsibleOptionRow
                key={option.QuoteOptionId}
                optionKey={optionKey}
                label={option.OptionDetails || option.OptionName || 'Option'}
                currentValue={currentValue}
                isDefault={isDefault}
                isExpanded={expandedOption === optionKey}
                onToggle={setExpandedOption}
                showDivider={!isLast}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="body1" fontWeight={500}>
                            {option.OptionDetails || option.OptionName || 'Option'}
                        </Typography>
                        {option.HelpText && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {option.HelpText}
                            </Typography>
                        )}
                    </Box>
                    <Box>
                        {useSegmented ? (
                            <SegmentedControl
                                options={getSegmentedOptions(option)}
                                value={option.ValueSelected}
                                onChange={(val) => handleGenericOptionUpdate(option.QuoteOptionId, val)}
                                disabled={updatingRef.current[optionKey]}
                            />
                        ) : (
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <Select
                                    value={option.ValueSelected.toString()}
                                    onChange={(e: SelectChangeEvent<string>) => {
                                        handleGenericOptionUpdate(
                                            option.QuoteOptionId,
                                            parseInt(e.target.value, 10)
                                        );
                                    }}
                                    disabled={updatingRef.current[optionKey]}
                                    sx={{ height: 40 }}
                                >
                                    {getDropdownOptions(option).map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value.toString()}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </Box>
            </CollapsibleOptionRow>
        );
    };

    // Render grouped option (like Leech Profile)
    const renderGroupedOption = (groupCode: string, group: { name: string; items: GenericOption[] }) => {
        const selectedItem = group.items.find(i => i.ValueSelected === 1);
        const helpText = group.items[0]?.HelpText;
        const optionKey = 'group_' + groupCode;
        const currentIndex = allOptionKeys.indexOf(optionKey);
        const isLast = currentIndex === allOptionKeys.length - 1;

        // Get current value display - extract just the value part after colon
        let currentValue = selectedItem?.OptionDetails || '';
        if (currentValue.includes(':')) {
            currentValue = currentValue.split(':').pop()?.trim() || currentValue;
        }

        // Check if current selection is the default
        const isDefault = selectedItem?.DefaultValue === 1;

        const segmentOptions = group.items.map(item => {
            const itemIsDefault = item.DefaultValue === 1;
            const priceDelta = item.OptionQuotedPrice * (1 - item.DefaultValue);
            let label = item.OptionDetails || '';
            if (label.includes(':')) {
                label = label.split(':').pop()?.trim() || label;
            }
            if (itemIsDefault) {
                label += ' ✓';
            } else if (priceDelta !== 0) {
                label += formatPriceDelta(priceDelta);
            }
            return { value: item.QuoteOptionId, label, isDefault: itemIsDefault };
        });

        return (
            <CollapsibleOptionRow
                key={groupCode}
                optionKey={optionKey}
                label={group.name}
                currentValue={currentValue}
                isDefault={isDefault}
                isExpanded={expandedOption === optionKey}
                onToggle={setExpandedOption}
                showDivider={!isLast}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="body1" fontWeight={500}>
                            {group.name}
                        </Typography>
                        {helpText && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {helpText}
                            </Typography>
                        )}
                    </Box>
                    <Box>
                        <SegmentedControl
                            options={segmentOptions}
                            value={selectedItem?.QuoteOptionId || 0}
                            onChange={(optionId) => handleGenericOptionUpdate(optionId, 1)}
                        />
                    </Box>
                </Box>
            </CollapsibleOptionRow>
        );
    };

    // Render battens row
    const renderBattensRow = () => {
        if (state.battenOptions.length === 0) return null;

        const currentValue = isFurling === 1 ? state.halfBattens : state.fullBattens;
        const label = isFurling === 1 ? 'Battens' : 'Full Battens';
        const optionKey = 'battens';
        const currentIndex = allOptionKeys.indexOf(optionKey);
        const isLast = currentIndex === allOptionKeys.length - 1;

        // Find the default batten (the one with CostDelta = 0)
        const defaultBatten = state.battenOptions.find(b => b.CostDelta === 0);
        const defaultValue = defaultBatten ? (isFurling === 1 ? defaultBatten.HalfBattens : defaultBatten.FullBattens) : -1;
        const isDefault = currentValue === defaultValue;

        // Get display text for current value
        const selectedBatten = state.battenOptions.find(b => 
            (isFurling === 1 ? b.HalfBattens : b.FullBattens) === currentValue
        );
        const displayValue = selectedBatten?.DisplayText || currentValue.toString();

        const segmentOptions = state.battenOptions.map(batten => {
            const battenValue = isFurling === 1 ? batten.HalfBattens : batten.FullBattens;
            const battenIsDefault = battenValue === defaultValue;
            let optLabel = batten.DisplayText;
            if (battenIsDefault) {
                optLabel += ' ✓';
            } else if (batten.CostDelta !== 0) {
                optLabel += formatPriceDelta(batten.CostDelta);
            }
            return { value: battenValue, label: optLabel, isDefault: battenIsDefault };
        });

        return (
            <CollapsibleOptionRow
                key="battens"
                optionKey={optionKey}
                label={label}
                currentValue={displayValue}
                isDefault={isDefault}
                isExpanded={expandedOption === optionKey}
                onToggle={setExpandedOption}
                showDivider={!isLast}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="body1" fontWeight={500}>
                            {label}
                        </Typography>
                    </Box>
                    <Box>
                        <SegmentedControl
                            options={segmentOptions}
                            value={currentValue}
                            onChange={handleBattenUpdate}
                            disabled={updatingRef.current.battens_full}
                        />
                    </Box>
                </Box>
            </CollapsibleOptionRow>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Cloth Selection */}
            {renderClothRow()}

            {/* Render battens first */}
            {renderBattensRow()}

            {/* Render all options in exact database order */}
            {renderList.map(item =>
                item.type === 'option'
                    ? renderOptionRow(item.option)
                    : renderGroupedOption(item.groupCode, item.group)
            )}
        </Box>
    );
};

export default OptionsDropdowns;
